import {
  createContext,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import type { MessageFilterOpts } from '@fv/models'

type MessageThread = {
  loadId: string
  carrierId?: string
}

type OpenMessagesPanelOptions = {
  thread?: MessageThread
  filter?: Partial<MessageFilterOpts>
  initialMessageId?: string
}

const defaultMessagesPanelFilter: MessageFilterOpts = {
  search: '',
  carrierId: '',
  status: 'all',
  unreadOnly: false,
  includeArchived: false,
}

type MessagesPanelContextValue = {
  closePanel: () => void
  filter: MessageFilterOpts
  initialMessageId?: string
  isPanelOpen: boolean
  openPanel: (options?: OpenMessagesPanelOptions) => void
  selectedThread?: MessageThread
  setFilter: Dispatch<SetStateAction<MessageFilterOpts>>
  selectThread: (thread: MessageThread | undefined) => void
  togglePanel: () => void
}

const MessagesPanelContext = createContext<
  MessagesPanelContextValue | undefined
>(undefined)

export const MessagesPanelProvider = ({ children }: PropsWithChildren) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const [filter, setFilter] = useState<MessageFilterOpts>(
    defaultMessagesPanelFilter,
  )
  const [initialMessageId, setInitialMessageId] = useState<string>()
  const [selectedThread, setSelectedThread] = useState<MessageThread>()

  const openPanel = useCallback((options?: OpenMessagesPanelOptions) => {
    if (options?.thread) setSelectedThread(options.thread)
    if (options?.filter) {
      setFilter({
        ...defaultMessagesPanelFilter,
        ...options.filter,
      })
    }
    setInitialMessageId(options?.initialMessageId)
    setIsPanelOpen(true)
  }, [])
  const closePanel = useCallback(() => {
    setIsPanelOpen(false)
    setFilter(defaultMessagesPanelFilter)
    setInitialMessageId(undefined)
    setSelectedThread(undefined)
  }, [])
  const togglePanel = useCallback(() => {
    isPanelOpen ? closePanel() : openPanel()
  }, [closePanel, openPanel, isPanelOpen])

  const value = useMemo<MessagesPanelContextValue>(
    () => ({
      closePanel,
      filter,
      initialMessageId,
      isPanelOpen,
      openPanel,
      selectedThread,
      setFilter,
      selectThread: setSelectedThread,
      togglePanel,
    }),
    [
      closePanel,
      filter,
      initialMessageId,
      isPanelOpen,
      openPanel,
      selectedThread,
      togglePanel,
    ],
  )

  return (
    <MessagesPanelContext.Provider value={value}>
      {children}
    </MessagesPanelContext.Provider>
  )
}

export const useMessagesPanel = () => {
  const context = useContext(MessagesPanelContext)
  if (!context) {
    throw new Error(
      'useMessagesPanel must be used within MessagesPanelProvider',
    )
  }
  return context
}
